import { CrewClaimTypeEnum } from 'corso-types';
import { Navigate } from 'react-router-dom';
import { useEnabledClaimType } from '~/hooks/useEnabledClaimType';
import { useWidgetRef } from '~/hooks/widgetHooks';
import { useStoreId } from '~/providers/StoreUserProvider';

/**
 * Given the current configuration, changes what the default route is when initially opening the app.
 * If a widgetRef is present, goes delegate to an integrations page.
 * Otherwise, goes to the following, in order: returns, warranties, shipping protection, registrations, analytics as a final fallback for the current store.
 * Resulting in the `storeId` of the current store being set into the path if it wasn't already.
 */
export default function DefaultStoreNavigation() {
  const storeId = useStoreId();
  const widgetRef = useWidgetRef();

  const {
    isLoading,
    isWarrantyEnabled,
    isReturnsEnabled,
    isShippingProtectionEnabled,
    isRegistrationEnabled,
  } = useEnabledClaimType();

  // ? possibly also check if it's in an iframe or embedded in any way
  if (widgetRef) return <Navigate to={`/${storeId}/integrations`} replace />;

  if (isLoading) return null; // ? might need some kind of better loader here, once this changes to use the `actions`, `loaders` model it'll be better

  if (isReturnsEnabled) {
    return (
      <Navigate
        to={`/${storeId}/claims/${CrewClaimTypeEnum.return.toLowerCase()}`}
        replace
      />
    );
  }

  if (isWarrantyEnabled) {
    return (
      <Navigate
        to={`/${storeId}/claims/${CrewClaimTypeEnum.warranty.toLowerCase()}`}
        replace
      />
    );
  }

  if (isShippingProtectionEnabled) {
    return <Navigate to={`/${storeId}/claims/shipping`} replace />;
  }

  if (isRegistrationEnabled) {
    return <Navigate to={`/${storeId}/registrations`} replace />;
  }

  // a final fallback, but this will be blocked for non-admins, but that's fine, since they wouldn't have anything active
  return <Navigate to={`/${storeId}/analytics`} replace />;
}
